import type { TFunction } from 'i18next';

import {
  CreateUpdatePurchaseInvoiceEstimateItemDto,
  GetEstimateItemDto,
  GetPurchaseInvoiceEstimateItemDto,
  GetPurchaseInvoiceExtendedDto,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import mathUtils from '../../../utils/mathUtils';
import { TKeys } from '../../../translations';

export const getSavedItem = (
  item: CreateUpdatePurchaseInvoiceEstimateItemDto,
  savedPurchaseInvoiceEstimateItems?: GetPurchaseInvoiceEstimateItemDto[],
) =>
  savedPurchaseInvoiceEstimateItems?.find(
    ({ estimateItemId }) => estimateItemId === item.estimateItemId,
  );

export const getPurchaseInvoiceItem = (
  availableEstimateItemId: string,
  purchaseInvoiceEstimateItems: CreateUpdatePurchaseInvoiceEstimateItemDto[],
) =>
  purchaseInvoiceEstimateItems.find(
    it => it.estimateItemId === availableEstimateItemId,
  ) || ({ amount: '0' } as CreateUpdatePurchaseInvoiceEstimateItemDto);

export const getNotAssignedAmount = (estimateItem: GetEstimateItemDto) =>
  mathUtils.subtract(
    mathUtils.round(estimateItem.purchaseNetTotal),
    mathUtils.round(estimateItem.purchaseInvoicesSum),
  ) || '0';

export interface SumData {
  purchaseNetTotalSum: number;
  notAssignedSum: number;
  amountSum: number;
  maxSum: number;
  unsettledAmount: number;
}

export const getSumData = (
  purchaseInvoiceEstimateItems: CreateUpdatePurchaseInvoiceEstimateItemDto[],
  availableEstimateItems: GetEstimateItemDto[],
  filteredAvailableEstimateItems: GetEstimateItemDto[],
  extendedPurchaseInvoice?: GetPurchaseInvoiceExtendedDto,
): SumData => {
  let purchaseNetTotalSum = 0;
  let notAssignedSum = 0;
  let amountSum = 0;
  let amountToExcludeFromMax = 0;

  for (const availableEstimateItem of filteredAvailableEstimateItems) {
    const notAssignedAmount = mathUtils.subtract(
      availableEstimateItem.purchaseNetTotal,
      availableEstimateItem.purchaseInvoicesSum,
    );

    if (availableEstimateItem) {
      purchaseNetTotalSum = mathUtils.add(
        purchaseNetTotalSum,
        availableEstimateItem.purchaseNetTotal,
      );
      notAssignedSum = mathUtils.add(notAssignedSum, notAssignedAmount);
    }
  }
  for (const availableEstimateItem of availableEstimateItems) {
    const item = purchaseInvoiceEstimateItems.find(
      ({ estimateItemId }) => estimateItemId === availableEstimateItem.id,
    );

    if (item) {
      amountSum = mathUtils.add(amountSum, item.amount);
    }
  }
  const availableEstimateItemsIds = availableEstimateItems.map(({ id }) => id);
  purchaseInvoiceEstimateItems.forEach(estimateItem => {
    if (!availableEstimateItemsIds.includes(estimateItem.estimateItemId)) {
      amountToExcludeFromMax = mathUtils.add(
        amountToExcludeFromMax,
        estimateItem.amount,
      );
    }
  });

  return {
    purchaseNetTotalSum,
    notAssignedSum,
    amountSum,
    unsettledAmount: mathUtils.subtract(
      +(extendedPurchaseInvoice?.amount || '0'),
      amountSum,
      amountToExcludeFromMax,
    ),
    maxSum: +(extendedPurchaseInvoice?.amount || '0'),
  };
};

export const checkAmountInfo = (
  t: TFunction<'fvzLng', 'fvzLng'>,
  tk: TKeys<'fvzLng'>,
  max?: string | number,
  amount?: string | number,
) => {
  if (max === '.') {
    max = 0;
  }
  if (amount === '.') {
    amount = 0;
  }
  const amountDiff = mathUtils.subtract(
    mathUtils.round(max, 2),
    mathUtils.round(amount, 2),
  );
  const info = t(tk.finance.sumExceedsInvoiceAmount);
  if (
    mathUtils.parse(mathUtils.round(max, 2)) *
      mathUtils.parse(mathUtils.round(amountDiff, 2)) <
    0
  ) {
    return [info];
  }

  return [];
};
